import { read } from './http';
import { MOCK_SCORECARDS } from './mock/scorecards.mock';
import type { FixtureScorecard, ScorecardPlayer } from '@/types';

/**
 * Fixture scorecards — the provider-backed match centre.
 *
 * A fixture that was never synced returns 404 rather than an empty scorecard,
 * so callers treat "no scorecard" as a normal state, not an error.
 */

/** GET /api/v1/football/fixtures/:fixtureId/scorecard */
export function getScorecard(
  fixtureId: number,
  signal?: AbortSignal,
): Promise<FixtureScorecard> {
  return read(
    `/football/fixtures/${fixtureId}/scorecard`,
    () => {
      const card = MOCK_SCORECARDS[fixtureId];
      if (!card) throw new Error('Fixture scorecard not found');
      return card;
    },
    signal,
  );
}

/**
 * The scorecard, or null when the fixture has never been synced.
 *
 * Most screens want "show it if we have it", and a 404 here is expected rather
 * than exceptional — an unplayed fixture simply has no scorecard yet.
 */
export async function findScorecard(
  fixtureId: number,
  signal?: AbortSignal,
): Promise<FixtureScorecard | null> {
  try {
    return await getScorecard(fixtureId, signal);
  } catch (cause) {
    // A cancelled request must keep propagating, or the caller would treat an
    // unmount as "this fixture has no scorecard".
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    return null;
  }
}

interface ScorecardPlayersResponse {
  fixtureId: number;
  players: ScorecardPlayer[];
  count: number;
}

/** GET /api/v1/football/fixtures/:fixtureId/scorecard/players */
export function getScorecardPlayers(
  fixtureId: number,
  teamId?: number,
  signal?: AbortSignal,
): Promise<ScorecardPlayersResponse> {
  const path = teamId
    ? `/football/fixtures/${fixtureId}/scorecard/players?teamId=${teamId}`
    : `/football/fixtures/${fixtureId}/scorecard/players`;

  return read(
    path,
    () => {
      const players = (MOCK_SCORECARDS[fixtureId]?.players ?? []).filter(
        (player) => !teamId || player.teamId === teamId,
      );
      return { fixtureId, players, count: players.length };
    },
    signal,
  );
}

/** GET /api/v1/football/fixtures/:fixtureId/scorecard/players/:playerId */
export function getScorecardPlayer(
  fixtureId: number,
  playerId: number,
  signal?: AbortSignal,
): Promise<{ fixtureId: number; player: ScorecardPlayer }> {
  return read(
    `/football/fixtures/${fixtureId}/scorecard/players/${playerId}`,
    () => {
      const player = MOCK_SCORECARDS[fixtureId]?.players.find(
        (row) => row.playerId === playerId,
      );
      if (!player) throw new Error('Player not found on fixture scorecard');
      return { fixtureId, player };
    },
    signal,
  );
}
